import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import type { BlogPost } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, RefreshCw, Calendar, ArrowRight, Star } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { seoFor } from "@/data/marketing-routes";
import { blogPostAuthor, blogPostDate, blogPostPath, formatBlogDate } from "@/data/blog";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";

const getBlogUrl = blogPostPath;

/**
 * Blog index (build F17). The API returns featured posts first, then newest
 * first; each card shows its date and, when set, its author.
 */
export default function BlogPage() {
  useSEO(seoFor("/blog"));

  const { data: posts, isLoading, error, refetch } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog-posts"],
  });

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="pt-28 md:pt-32 pb-16 md:pb-24">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="text-blog-heading">
              PadPulse Blog
            </h1>
            <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto" data-testid="text-blog-subtitle">
              Insights and strategies for modern property management
            </p>
          </div>
        </section>
      </div>

      <main className="flex-grow">
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 min-h-[400px]" data-testid="blog-loading">
                <Loader2 className="h-10 w-10 animate-spin text-brand mb-4" />
                <p className="text-gray-500 text-base">Loading latest articles...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 min-h-[400px] text-center max-w-md mx-auto" data-testid="blog-error">
                <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-6">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Posts</h3>
                <p className="text-gray-500 mb-6 text-sm">Could not retrieve articles. Please try again.</p>
                <Button onClick={() => refetch()} className="bg-brand hover:bg-brand-hover text-white" data-testid="button-retry">
                  <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                </Button>
              </div>
            ) : !posts || posts.length === 0 ? (
              <div className="text-center py-20 min-h-[400px] flex flex-col items-center justify-center" data-testid="blog-empty">
                <p className="text-gray-500 text-lg font-medium">No blog posts yet.</p>
                <p className="text-gray-500 mt-2 text-sm">Check back soon for new content!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <Card key={post.id} className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col overflow-hidden group" data-testid={`card-blog-${post.id}`}>
                    {post.imageUrl && (
                      <Link href={getBlogUrl(post)}>
                        <div className="overflow-hidden">
                          <img
                            src={post.imageUrl}
                            alt={post.title}
                            className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                            data-testid={`img-blog-${post.id}`}
                          />
                        </div>
                      </Link>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2" data-testid={`text-blog-meta-${post.id}`}>
                        {post.featured && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-900" data-testid={`badge-featured-${post.id}`}>
                            <Star className="w-3 h-3" /> Featured
                          </span>
                        )}
                        <Calendar className="w-3.5 h-3.5" />
                        {formatBlogDate(blogPostDate(post))}
                        {blogPostAuthor(post) && <span>· {blogPostAuthor(post)}</span>}
                      </div>
                      <CardTitle className="text-lg text-gray-900 leading-snug" data-testid={`text-blog-title-${post.id}`}>
                        {post.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col justify-between gap-4">
                      <p className="text-gray-500 text-sm line-clamp-3">{post.excerpt || post.metaDescription || ""}</p>
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {post.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-xs bg-blue-50 text-brand px-2 py-0.5 rounded-md font-medium" data-testid={`tag-${tag}`}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <Link href={getBlogUrl(post)}>
                        <Button variant="ghost" className="text-brand hover:text-brand-hover hover:bg-blue-50 p-0 h-auto font-medium text-sm" data-testid={`button-read-${post.id}`}>
                          Read More <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
